import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://bensmithdev.com';
const DEFAULT_IMAGE = `${SITE_URL}/ben.jpeg`;

interface SeoProps {
  title: string;
  description: string;
  /** Route path, e.g. "/" or "/kiosks" */
  path: string;
  image?: string;
  /** Optional page-specific JSON-LD object(s) */
  jsonLd?: object | object[];
  /** Keep the page out of search indexes (outreach-only pages, 404s) */
  noindex?: boolean;
}

/**
 * Per-page head tags. public/index.html deliberately carries no description,
 * canonical, or social tags so prerendered pages never ship duplicates.
 */
const Seo: React.FC<SeoProps> = ({ title, description, path, image = DEFAULT_IMAGE, jsonLd, noindex }) => {
  const url = path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Ben Smith Development" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default Seo;
